import { useState, useEffect } from 'react'
import { getProducts } from '@services/product.service'
import useShopFilters from '@hooks/useShopFilters'
import FilterSidebar from '@components/Shop/FilterSidebar'
import ProductCard from '@components/Shop/ProductCard'
import Pagination from '@components/Shop/Pagination'
import Spinner from '@components/UI/Spinner'
import './ShopPage.scss'

const VISUAL_TYPES = ['sachet', 'hex', 'burner']
const PAGE_SIZE = 12
const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Cele mai noi' },
  { value: 'price_asc', label: 'Preț crescător' },
  { value: 'price_desc', label: 'Preț descrescător' },
  { value: 'name_asc', label: 'Nume A–Z' },
]

export default function ShopPage() {
  const { filters, setFilter, resetFilters } = useShopFilters()
  const [products, setProducts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const params = {
      page: filters.page,
      pageSize: PAGE_SIZE,
      sort: filters.sort,
    }
    if (filters.search) params.search = filters.search
    if (filters.categorySlug) params.categorySlug = filters.categorySlug
    if (filters.minPrice != null) params.minPrice = filters.minPrice
    if (filters.maxPrice != null) params.maxPrice = filters.maxPrice

    getProducts(params)
      .then((res) => {
        if (cancelled) return
        const data = res?.data ?? {}
        setProducts(data.items ?? [])
        setTotal(data.total ?? 0)
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([])
          setTotal(0)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [filters.search, filters.categorySlug, filters.minPrice, filters.maxPrice, filters.sort, filters.page])

  const hasResults = products.length > 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="shop-page">
      <header className="shop-hero">
        <div className="section-label">Magazin</div>
        <h1 className="section-title shop-title">
          Descoperă <em>colecția</em>
        </h1>
      </header>

      <div className="shop-layout">
        <div className={`shop-sidebar-wrap ${sidebarOpen ? 'open' : ''}`}>
          <div className="shop-sidebar-backdrop" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
          <div className="shop-sidebar">
            <FilterSidebar filters={filters} setFilter={setFilter} resetFilters={resetFilters} />
          </div>
        </div>

        <div className="shop-main">
          <div className="shop-toolbar">
            <button
              type="button"
              className="shop-filters-toggle"
              onClick={() => setSidebarOpen((o) => !o)}
              aria-expanded={sidebarOpen}
            >
              Filtre
            </button>
            <div className="shop-search-sort">
              <input
                type="search"
                className="shop-search"
                placeholder="Caută produse..."
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
                aria-label="Caută produse"
              />
              <select
                className="shop-sort"
                value={filters.sort}
                onChange={(e) => setFilter('sort', e.target.value)}
                aria-label="Sortează după"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="shop-loading">
              <Spinner />
            </div>
          ) : !hasResults ? (
            <div className="shop-empty">
              <p>Nu am găsit produse.</p>
              <p className="shop-empty-hint">Încearcă să modifici filtrele sau căutarea.</p>
            </div>
          ) : (
            <>
              <p className="shop-count">
                {total} {total === 1 ? 'produs' : 'produse'}
              </p>
              <div className="shop-grid">
                {products.map((p, i) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    visualType={VISUAL_TYPES[i % VISUAL_TYPES.length]}
                    index={i}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination
                  page={filters.page}
                  totalItems={total}
                  pageSize={PAGE_SIZE}
                  onPageChange={(p) => setFilter('page', p)}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
